import { useState } from 'react'
import { PageContainer, ProTable } from '@ant-design/pro-components'
import { hashKey, keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, App, Button } from 'antd'
import { ChevronDown, ListChecks } from 'lucide-react'
import type { z } from 'zod'
import type { SessionAccount } from '@super-mega/server/contracts/auth'
import type { BillCreateInput, billSchema } from '@super-mega/server/contracts/bill'
import { useMemberCreatorOptions } from '@/features/access/index.ts'
import { AuthorizedDropdown } from '@/features/authorization/authorized-dropdown.tsx'
import { Authorized } from '@/features/authorization/authorized.tsx'
import { usePageTableNavigation } from '@/hooks/index.ts'
import type { PageTableNavigate } from '@/hooks/use-page-table.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getCreatedAtSearch, getDateRangeFormValue, type DateRangeFormValue } from '@/utils/list-query.ts'
import { BillActionDialog, type BillOperation } from './bill-action-dialog.tsx'
import { BillDetail } from './bill-detail.tsx'
import { BillEditor } from './bill-editor.tsx'
import { createBillListColumns } from './bill-list-columns.tsx'
import { BillSearchBatchTask, type BillSearchBatchTaskInfo } from './bill-search-batch-task.tsx'
import { DEFAULT_BILL_SEARCH, type BillSearch } from './bill-search.ts'
import { BillSourcePicker } from './bill-source-picker.tsx'
import { BILL_ACTION_ITEMS } from './const/bill-actions.ts'
import { GroupDetail } from './group-detail.tsx'
import { useBillFilterOptions } from './hooks/use-bill-filter-options.ts'
import { RentalBillingPanel } from './rental-billing-panel.tsx'
import { refreshMoneyQueries } from './utils/refresh-money-queries.ts'

type Row = z.infer<typeof billSchema>
type Filters = {
  id?: number
  grouped?: BillSearch['grouped']
  archived?: BillSearch['archived']
  creatorMemberId?: number
  orderType?: BillSearch['orderType']
  keyword?: string
  note?: string
  customerId?: BillSearch['customerId']
  salespersonId?: BillSearch['salespersonId']
  organizationId?: BillSearch['organizationId']
  businessId?: BillSearch['businessId']
  collectionStatus?: BillSearch['collectionStatus']
  status?: BillSearch['status']
  createdAtRange?: DateRangeFormValue
  receivedAtRange?: DateRangeFormValue
}

function arrayFilter<T>(value: T | T[] | undefined) {
  if (value === undefined) {
    return undefined
  }
  if (Array.isArray(value)) {
    return value
  }
  return [value]
}

export function BillList({
  currentSession,
  search,
  navigateSearch: navigate,
  customerScoped = false,
  hideCreatedTimeFilter = false,
  defaultSearch = DEFAULT_BILL_SEARCH
}: {
  currentSession: SessionAccount
  search: BillSearch
  navigateSearch: PageTableNavigate<BillSearch>
  hideCreatedTimeFilter?: boolean
  customerScoped?: boolean
  defaultSearch?: BillSearch
}) {
  const trpc = useTRPC()
  const cache = useQueryClient(),
    { message } = App.useApp()
  const selectionQueryKey = hashKey(trpc.bill.list.queryKey({ ...search, direction: 'receivable' }))
  const [selectedQueryKey, setSelectedQueryKey] = useState(selectionQueryKey)
  const [selected, setSelected] = useState<Row[]>([])
  // 查询范围切换立即清空选择，覆盖快捷筛选和历史导航；相同查询的刷新保留勾选。
  if (selectedQueryKey !== selectionQueryKey) {
    setSelectedQueryKey(selectionQueryKey)
    setSelected([])
  }
  const [operation, setOperation] = useState<BillOperation>()
  const [batchTasks, setBatchTasks] = useState<BillSearchBatchTaskInfo[]>([])
  const [groupId, setGroupId] = useState<number>()
  const initialService = useMutation(trpc.bill.initialServiceBill.mutationOptions())
  const refreshFinance = async (notesOnly = false) => {
    await refreshMoneyQueries(cache, trpc, notesOnly ? 'notes' : 'finance')
    setSelected([])
    const refreshed = cache.getQueryData(trpc.bill.list.queryKey({ ...search, direction }))
    if (refreshed) {
      const lastPage = Math.max(1, Math.ceil(refreshed.total / search.pageSize))
      if (search.current > lastPage) {
        await navigate({ replace: true, search: (previous) => ({ ...previous, current: lastPage }) })
      }
    }
  }
  const direction = 'receivable' as const
  const canManage =
    currentSession.isSuperAdmin ||
    currentSession.permissions.some((permission) => permission.startsWith('bill.') && !permission.endsWith('.read'))
  const query = useQuery({
    ...trpc.bill.list.queryOptions({ ...search, direction }),
    placeholderData: keepPreviousData
  })
  const filterOptions = useBillFilterOptions(search)
  const creators = useMemberCreatorOptions('bill.read', search.creatorMemberId)
  const [detailId, setDetailId] = useState<number>()
  const [detailAction, setDetailAction] = useState<'refund'>()
  const [creating, setCreating] = useState(false)
  const [selecting, setSelecting] = useState(false)
  const [chosenSource, setChosenSource] = useState<BillCreateInput['source']>()
  const table = usePageTableNavigation<BillSearch, Filters>({
    search,
    navigate,
    defaultSearch: { ...defaultSearch, direction },
    onQueryChange: () => setSelected([]),
    showTotal: (total) => `共 ${total} 张账单`,
    getFormInitialValues: (value) => ({
      id: undefined,
      grouped: value.grouped,
      archived: value.archived,
      keyword: value.keyword ?? (value.id ? String(value.id) : undefined),
      note: value.note,
      creatorMemberId: value.creatorMemberId,
      orderType: value.orderType,
      collectionStatus: arrayFilter(value.collectionStatus),
      status: arrayFilter(value.status),
      customerId: arrayFilter(value.customerId),
      salespersonId: arrayFilter(value.salespersonId),
      organizationId: arrayFilter(value.organizationId),
      businessId: arrayFilter(value.businessId),
      createdAtRange: getDateRangeFormValue(value.createdFrom, value.createdTo),
      receivedAtRange: getDateRangeFormValue(value.receivedFrom, value.receivedTo)
    }),
    getSearchFromForm: (value) => {
      const receiptRange = getCreatedAtSearch(value.receivedAtRange)
      return {
        // 报表下钻仍可携带这些条件，手动查询时清除已移除的筛选项。
        id: undefined,
        feeType: undefined,
        settlement: undefined,
        grouped: value.grouped,
        direction,
        archived: value.archived ?? 'active',
        siteId: undefined,
        periodFrom: undefined,
        periodTo: undefined,
        keyword: value.keyword?.trim() || undefined,
        note: value.note?.trim() || undefined,
        creatorMemberId: value.creatorMemberId,
        orderType: value.orderType,
        collectionStatus: value.collectionStatus,
        status: value.status,
        customerId: value.customerId,
        salespersonId: value.salespersonId,
        organizationId: value.organizationId,
        businessId: value.businessId,
        ...getCreatedAtSearch(value.createdAtRange),
        receivedFrom: receiptRange.createdFrom,
        receivedTo: receiptRange.createdTo
      }
    }
  })
  const canReadOrders =
    currentSession.isSuperAdmin ||
    currentSession.permissions.some(
      (permission) => permission === 'order.rental.read' || permission === 'order.service.read'
    )
  const columns = createBillListColumns({
    search,
    filterOptions,
    creators,
    canReadOrders,
    canManage,
    setDetailAction,
    setDetailId,
    setOperation
  })
  let filteredSource: BillCreateInput['source'] | undefined
  if (search.rentalOrderId && !search.serviceOrderId) {
    filteredSource = { type: 'rental', orderId: search.rentalOrderId }
  } else if (search.serviceOrderId && !search.rentalOrderId) {
    filteredSource = { type: 'service', orderId: search.serviceOrderId }
  }
  const source = chosenSource ?? filteredSource
  let sourceLabel: string | undefined
  if (search.rentalOrderId) {
    sourceLabel = `租赁订单 #${search.rentalOrderId}`
  } else if (search.serviceOrderId) {
    sourceLabel = `服务订单 #${search.serviceOrderId}`
  }
  return (
    <PageContainer
      title="收款账单"
      breadcrumbRender={false}
      content="按费用期间核对应收金额及实际收款，租赁的每项费用、每个账期分别出账。"
    >
      {query.isError && (
        <Alert
          className="mb-4"
          type="error"
          showIcon
          title={query.error.message}
          action={<Button onClick={() => void query.refetch()}>重试</Button>}
        />
      )}
      {sourceLabel && (
        <Alert
          className="mb-4"
          type="info"
          showIcon
          title={`当前仅显示 ${sourceLabel} 的账单`}
          action={
            <Button
              onClick={() =>
                void navigate({
                  replace: true,
                  search: (previous) => ({
                    ...previous,
                    rentalOrderId: undefined,
                    serviceOrderId: undefined,
                    current: 1
                  })
                })
              }
            >
              清除订单筛选
            </Button>
          }
        />
      )}
      {search.rentalOrderId && canManage && (
        <RentalBillingPanel rentalOrderId={search.rentalOrderId} onBill={setDetailId} />
      )}
      {search.serviceOrderId && canManage && (
        <Authorized permissions={['bill.generate']}>
          <Button
            className="mb-4"
            loading={initialService.isPending}
            onClick={async () => {
              try {
                const b = await initialService.mutateAsync({ serviceOrderId: search.serviceOrderId! })
                await refreshFinance()
                setDetailId(b.id)
              } catch (e) {
                void message.error(e instanceof Error ? e.message : '出账失败')
              }
            }}
          >
            生成／查看服务初始账单
          </Button>
        </Authorized>
      )}
      {batchTasks.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {batchTasks.map((task) => (
            <BillSearchBatchTask key={task.jobId} task={task} onSaved={() => refreshFinance(task.action === 'note')} />
          ))}
        </div>
      )}
      <ProTable<Row, Filters>
        columns={columns.filter(
          (column) =>
            (!customerScoped || column.dataIndex !== 'customerId') &&
            (!hideCreatedTimeFilter || column.dataIndex !== 'createdAtRange')
        )}
        rowSelection={
          canManage
            ? {
                selectedRowKeys: selected.map((r) => r.id),
                onChange: (_, rows) => setSelected(rows)
              }
            : undefined
        }
        rowKey="id"
        dataSource={query.data?.data ?? []}
        loading={query.isFetching}
        headerTitle={`收款账单列表（${query.data?.total ?? 0}）`}
        columnsState={{
          persistenceKey: customerScoped ? 'customer-documents-bills:table' : 'bills:table:v2',
          persistenceType: 'localStorage'
        }}
        scroll={{ x: 'max-content' }}
        formRef={table.formRef}
        form={{ initialValues: table.formInitialValues }}
        onSubmit={table.onSubmit}
        onReset={table.onReset}
        search={{
          labelWidth: 'auto',
          defaultCollapsed: customerScoped,
          defaultFormItemsNumber: 2,
          collapseRender: (collapsed) => <span className="whitespace-nowrap">{collapsed ? '展开' : '收起'}</span>
        }}
        pagination={{ ...table.pagination, total: query.data?.total ?? 0 }}
        options={{ reload: () => void query.refetch() }}
        onChange={
          /* eslint-disable max-params -- Ant Design Table 的回调签名由组件定义。 */
          (_, __, sorter, extra) => {
            if (extra.action !== 'sort') {
              return
            }
            const sort = Array.isArray(sorter) ? sorter[0] : sorter
            void navigate({
              replace: true,
              search: (previous) => ({
                ...previous,
                current: 1,
                sortField: sort.field === 'dueAt' ? 'dueAt' : 'createdAt',
                sortOrder: sort.order === 'ascend' ? 'asc' : 'desc'
              })
            })
          }
          /* eslint-enable max-params */
        }
        toolBarRender={() =>
          canManage
            ? [
                ...(['selected', 'search'] as const).map((mode) => (
                  <AuthorizedDropdown
                    resource="bill"
                    key={mode}
                    trigger={['click']}
                    placement="bottomRight"
                    disabled={query.isFetching || (mode === 'selected' ? !selected.length : !query.data?.total)}
                    menu={{
                      items: BILL_ACTION_ITEMS.map((item) => ({
                        ...item,
                        label: item.key === 'note' ? '批量添加备注' : item.label
                      })),
                      onClick: ({ key }) =>
                        setOperation({
                          action: key as BillOperation['action'],
                          total: mode === 'search' ? query.data?.total : undefined,
                          target:
                            mode === 'selected'
                              ? { mode, ids: selected.map((row) => row.id), direction }
                              : { mode, search: { ...search, direction } }
                        })
                    }}
                  >
                    <Button
                      disabled={query.isFetching || (mode === 'selected' ? !selected.length : !query.data?.total)}
                      icon={mode === 'selected' ? <ListChecks aria-hidden size={16} /> : undefined}
                    >
                      {mode === 'selected' ? '批量操作' : '按搜索批量操作'} <ChevronDown aria-hidden size={14} />
                    </Button>
                  </AuthorizedDropdown>
                )),
                ...(source || canReadOrders
                  ? [
                      <Authorized key="new" permissions={['bill.create']}>
                        <Button
                          type="primary"
                          key="new"
                          onClick={() => (source ? setCreating(true) : setSelecting(true))}
                        >
                          新建应收
                        </Button>
                      </Authorized>
                    ]
                  : [])
              ]
            : []
        }
      />
      {operation && (
        <BillActionDialog
          operation={operation}
          onQueued={(task) => {
            setBatchTasks((previous) => [...previous, task])
            setOperation(undefined)
          }}
          onClose={() => setOperation(undefined)}
          onSaved={() => refreshFinance(operation.action === 'note')}
          onGroup={setGroupId}
        />
      )}
      {groupId && <GroupDetail id={groupId} canManage={canManage} onClose={() => setGroupId(undefined)} />}
      {detailId && (
        <BillDetail
          key={detailId}
          billId={detailId}
          initialAction={detailAction}
          onDeleted={() => refreshFinance()}
          canManage={canManage}
          onClose={() => {
            setDetailId(undefined)
            setDetailAction(undefined)
          }}
        />
      )}
      {selecting && (
        <BillSourcePicker
          onClose={() => setSelecting(false)}
          onSelect={(value) => {
            setChosenSource(value)
            setSelecting(false)
            setCreating(true)
          }}
        />
      )}
      {creating && source && (
        <BillEditor
          source={source}
          onClose={() => {
            setCreating(false)
            setChosenSource(undefined)
          }}
          onSaved={(id) => {
            setCreating(false)
            setChosenSource(undefined)
            setDetailId(id)
          }}
        />
      )}
    </PageContainer>
  )
}
